// near-fast-fin-transfer (EVM origin) — RELAYER-ECONOMIC action.
//
// A fast-finalize doesn't wait for the slow light-client proof: the relayer
// (the connected NEAR account) FRONTS THEIR OWN nToken liquidity to the
// recipient immediately, and is reimbursed later when the real proof
// finalises. So this is NOT a generic operator rescue — running it spends
// the signer's funds. The UI gates it behind the relayer's NEAR wallet and
// a prominent warning.
//
// Build: read the origin EVM `InitTransfer` event (no proof needed — the
// relayer takes the event on faith and the contract reconciles later),
// denormalize the amount to the NEAR token's decimals, preflight the
// relayer's balance, then `buildFastFinTransfer` → `ft_transfer_call` on the
// nToken to the omni-bridge. Mirrors bridge-sdk-rs `near_fast_transfer`.

import { ChainKind } from "@omni-bridge/core";
import { createNearBuilder } from "@omni-bridge/near";
import { parseInitTransferEvent } from "@omni-bridge/evm";

import { nearView } from "@/api/client";
import { chains } from "@/config/chains";
import type { Network } from "@/config/network";
import type { OmniAddress } from "@omni-bridge/core";
import type { UnsignedNearTx } from "@/types/dto";
import { omniBridgeContract, FinTransferUnsupported } from "@/wallet/finTransfer";
import { sdkTxToUnsignedNearTx } from "@/wallet/sdkTx";

type EvmKey = "eth" | "arb" | "base" | "bnb" | "pol";

const EVM_ORIGINS: Record<EvmKey, { chainKind: ChainKind; prefix: string }> = {
  eth: { chainKind: ChainKind.Eth, prefix: "eth" },
  arb: { chainKind: ChainKind.Arb, prefix: "arb" },
  base: { chainKind: ChainKind.Base, prefix: "base" },
  bnb: { chainKind: ChainKind.Bnb, prefix: "bnb" },
  pol: { chainKind: ChainKind.Pol, prefix: "pol" },
};

const NAME_TO_EVM_KEY: Record<string, EvmKey> = {
  Eth: "eth",
  Arb: "arb",
  Base: "base",
  Bnb: "bnb",
  Polygon: "pol",
};

export interface FastFinArgs {
  network: Network;
  /** PascalCase origin chain (EVM only — fast-fin from Sol/UTXO is separate). */
  originChain: string;
  txHash: string;
  /** Optional storage deposit for the recipient's token registration. */
  storageDepositAmount: bigint | null;
  /** Connected NEAR account = the relayer fronting liquidity. */
  relayer: string;
}

interface TokenDecimals {
  decimals: number;
  origin_decimals: number;
}

export async function buildFastFinTransfer(args: FastFinArgs): Promise<{ tx: UnsignedNearTx }> {
  const { network, txHash, relayer } = args;
  const key = NAME_TO_EVM_KEY[args.originChain];
  if (!key) {
    throw new FinTransferUnsupported(
      `Fast-fin from ${args.originChain} isn't supported client-side (EVM origins only; ` +
        `Solana/UTXO fast-fin use different paths). Run bridge-cli for those.`,
    );
  }
  const { chainKind, prefix } = EVM_ORIGINS[key];

  // 1. Read + decode the origin InitTransfer event (no proof — the relayer
  //    fronts on the event; the contract reconciles when the real proof lands).
  const rpcUrl = chains(network).evm[key].rpcUrl;
  const { createPublicClient, http } = await import("viem");
  const client = createPublicClient({ transport: http(rpcUrl) });
  const receipt = await client.getTransactionReceipt({ hash: txHash as `0x${string}` });
  const ev = parseInitTransferEvent(receipt.logs);
  if (ev.amount < ev.fee) {
    throw new Error(`Transfer amount (${ev.amount}) is less than fee (${ev.fee}).`);
  }

  // 2. Resolve the NEAR token id + decimals.
  const omni = await omniBridgeContract();
  const tokenOmni = `${prefix}:${ev.tokenAddress}`;
  const tokenId = await nearView<string>(omni, "get_token_id", { address: tokenOmni });
  const decimals = await nearView<TokenDecimals>(omni, "get_token_decimals", { address: tokenOmni });

  // 3. Denormalize (amount - fee) from origin decimals → NEAR token decimals.
  //    Mirrors bridge-sdk-rs `denormalize_amount`: × 10^(origin_decimals - decimals).
  //    The contract guarantees origin_decimals >= decimals; guard anyway.
  if (decimals.origin_decimals < decimals.decimals) {
    throw new Error(
      `Unexpected decimals (origin ${decimals.origin_decimals} < near ${decimals.decimals}) — refusing to guess the send amount.`,
    );
  }
  const scale = 10n ** BigInt(decimals.origin_decimals - decimals.decimals);
  const amountToSend = (ev.amount - ev.fee) * scale;

  // 4. Preflight the relayer's balance — fast-fin spends THEIR funds.
  const balanceRaw = await nearView<string>(tokenId, "ft_balance_of", { account_id: relayer });
  const balance = BigInt(balanceRaw ?? "0");
  if (balance < amountToSend) {
    throw new Error(
      `Insufficient relayer balance: have ${balance} ${tokenId}, need ${amountToSend} to front this transfer.`,
    );
  }

  // 5. Storage deposit for the recipient (if a NEAR account that isn't registered).
  let storageDepositAmount = args.storageDepositAmount;
  if (storageDepositAmount === null && ev.recipient.startsWith("near:")) {
    const builder0 = createNearBuilder({ network });
    const required = await builder0.getRequiredStorageDeposit(ev.recipient.slice("near:".length));
    storageDepositAmount = required > 0n ? required : null;
  }

  // 6. Build the ft_transfer_call (nToken → omni-bridge with the FastFinTransfer msg).
  const builder = createNearBuilder({ network });
  const unsigned = builder.buildFastFinTransfer(
    {
      tokenId,
      amount: ev.amount.toString(),
      amountToSend: amountToSend.toString(),
      transferId: { origin_chain: chainKind, origin_nonce: ev.originNonce.toString() },
      recipient: ev.recipient as OmniAddress,
      fee: { fee: ev.fee.toString(), native_fee: ev.nativeTokenFee.toString() },
      msg: ev.message,
      storageDepositAmount: storageDepositAmount === null ? undefined : storageDepositAmount.toString(),
      relayer,
    },
    relayer,
  );
  return { tx: sdkTxToUnsignedNearTx(unsigned) };
}
